<template>
  <div class="wt-timeline-adapter">
    <tm-timemachine
      :showEmptyView="showEmptyView"
      :lightboxIsEmpty="lightboxIsEmpty"
      @ready="$_tmReady"
      @callbackChange="callbackChange"
      @callbackRCPSeek="callbackRCPSeek"
      @callbackSecondhandDragStop="callbackSecondhandDragStop"
      @callbackAssetResizeStart="callbackAssetResizeStart"
      @callbackAudioAssetResizeStart="callbackAudioAssetResizeStart"
      @callbackGetFragmentCutTimes="callbackGetFragmentCutTimes"
      @callbackAssetResizeUpdate="callbackAssetResizeUpdate"
      @callbackAssetResizeStop="callbackAssetResizeStop"
      @callbackAssetRemoved="callbackAssetRemoved"
      @callbackAudioRemoved="callbackAudioRemoved"
      @callbackEditAsset="callbackEditAsset"
      @callbackEditSegment="callbackEditSegment"
      @callbackEditTranscript="callbackEditTranscript"
      @callbackAudioAssetSplitStart="callbackAudioAssetSplitStart"
      @callbackAssetSplitStart="callbackAssetSplitStart"
      @callbackAssetSplitStop="callbackAssetSplitStop"
      @callbackBeforeDragStarts="callbackBeforeDragStarts"
      @callbackBeforeAddingExternalAsset="callbackBeforeAddingExternalAsset"
      @callbackAfterAddingExternalAsset="callbackAfterAddingExternalAsset"
      @callbackEnterSceneEdit="callbackEnterSceneEdit"
      @callbackLeaveSceneEdit="callbackLeaveSceneEdit"
      @callbackApplySceneEdit="callbackApplySceneEdit"
      @callbackCancelSceneEdit="callbackCancelSceneEdit"
      @callbackSceneOverlayInserted="callbackSceneOverlayInserted"
      @callbackSceneUpdate="callbackSceneUpdate"
      @callbackOverlayResizeStart="callbackOverlayResizeStart"
      @callbackOverlayResizeUpdate="callbackOverlayResizeUpdate"
      @callbackOverlayResizeStop="callbackOverlayResizeStop"
    />
  </div>
</template>

<script>
import app from 'app';
import { mapState, mapActions, mapGetters } from 'vuex';
import _ from 'lodash-extended';
import storyViewBus from '../storyViewBus';
import TimelineGatewayController from '../../../controllers/TimelineGatewayController';
import tmTimemachine from './tmTimemachine.vue';
import fg from '#/libs/FrameGrid';
import tmBus from './api/tmBus';
import wtTranscribe from 'v/Transcribe/Transcribe.vue';
import Analytics from 'c/WtAnalytics';
import audioNotification from 'c/audioNotification';
import { getTranscriptEditableAssets } from 'v/Story/Timeline/api/asset';
import { workPool } from '@wochitnpm/helpers';
import { getOriginalVideoFromFragment } from 'AssetEditor/store/assetEditorApi';
import AssetModal from 'v/assetModal/AssetModal.vue';
import AssetEditorStore from 'AssetEditor/store/AssetEditorStore';
import { createOverlayFromExternalAsset } from './api/model';

export default {
  name: 'wtTimelineAdapter',
  components: {
    tmTimemachine,
  },

  data() {
    return {
      workPoolHandlers: new Set(),
    };
  },

  computed: {
    ...mapState({
      timelineZoom: (state) => state.story.timelineZoom,
      timelineDuration: (state) => state.story.timelineDuration,
      selectedAssets: (state) => state.story.selectedAssets,
      selectedVoiceOverSegments: (state) =>
        state.story.selectedVoiceOverSegments,
      audioTracks: (state) => state.story.audioTracks,
    }),
    ...mapState('timemachine', {
      tmSecondhand: (state) => state.secondhand,
      tmMODE: (state) => state.MODE,
      tmSelected: (state) => state.selected,
      tmSceneEscort: (state) => state.sceneEscort,
      trackLayer: (state) => state.trackLayer,
    }),
    ...mapGetters('timemachine', ['isBusy', 'visualsWithExistingTranscript']),

    isTimelineEmpty() {
      return this.timelineDuration === 0;
    },

    showEmptyView() {
      return (
        this.isTimelineEmpty &&
        !this.tmMODE.VISUAL_DND &&
        !this.tmMODE.AUDIO_DND
      );
    },

    lightboxIsEmpty() {
      return (
        _.isEmpty(this.selectedAssets) &&
        _.isEmpty(this.selectedVoiceOverSegments)
      );
    },
  },

  beforeDestroy() {
    app.realTimePreviewController.off();
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.TIMELINE);
    storyViewBus.neverMind(this);
    workPool.off(this.$data.workPoolHandlers);
  },

  mounted() {
    this.$once('timemachine-is-ready', (a) => {
      app.timelineGateway.whenIsTuned.then(() => {
        // timemachine tracks are ready for querying
        app.timelineGateway.whenWidgetReady.resolve();
        // lightbox assets are ready for drag
        app.rootStore.commit('triggerReDraggableAssets');
        app.timelineGateway.updateUsageMarkers();
      });
    });

    workPool.on(
      { type: workPool.job.audiowaveform.type },
      (payload) => {
        workPool.job.audiowaveform.loadWaveformData(payload);
      },
      this.$data.workPoolHandlers
    );

    workPool.on(
      { type: workPool.job.snapBeats.type },
      (payload) => {
        if (payload.status === 'DONE') {
          this.SET_BG_MUSIC_BEATS(payload);
        } else if (payload.status === 'PROGRESS') {
          this.SET_BG_MUSIC_BEATS_STATUS_PROGRESS();
        }
      },
      this.$data.workPoolHandlers
    );

    storyViewBus.mind(this, [
      'svbTimelineUpdate',
      'svbTimelineZoom',
      'svbTimelineRedrawAsset',
      'svbTimelineHandleWallpaperReplace',
      'svbTimelineAfterAssetEdit',
      'svbTimelineRtp',
      'svbRcpIsPlaying',
      'svbRcpCurrentTime',
      'svbAppendToTimeline',
      'svbRemoveAudioTrack',
      'svbShowVisualAudioGap',
    ]);

    app.keyboardAccessibility.add(app.KEYBOARD_LAYER.TIMELINE, [
      {
        on: 'keydown',
        key: $.ui.keyCode.LEFT,
        run: this.$_timelineSeek,
      },
      {
        on: 'keydown',
        key: $.ui.keyCode.RIGHT,
        run: this.$_timelineSeek,
      },
      {
        on: 'keydown',
        key: $.ui.keyCode.BACKSPACE,
        run: this.$_timelineRemoveAsset,
        stash: { toTheLeft: true },
      },
      {
        on: 'keydown',
        key: $.ui.keyCode.DELETE,
        run: this.$_timelineRemoveAsset,
        stash: { toTheLeft: false },
      },
    ]);
  },

  watch: {
    isBusy: '$_tmBusy',
  },

  methods: {
    ...mapActions('timemachine', [
      'SET_SECONDHAND',
      'SET_MODE',
      'CHANGE_ROW_DURATION',
      'SET_SCROLL_TOP',
      'UPDATE_TIMEMACHINE_DATA',
      'VISUAL_RTP_STATUS_CHANGED',
      'REMOVE_VISUAL',
      'REMOVE_VISUAL_OVERLAY',
      'UX_AFTER_REMOVE_VISUAL',
      'REMOVE_AUDIO',
      'UX_AFTER_REMOVE_AUDIO',
      'SELECT_VISUAL',
      'SELECT_OVERLAY',
      'UNSELECT_TRACKS',
      'APPLY_EDITED_ASSET',
      'INSERT_VISUAL_ASSET',
      'INSERT_AUDIO',
      'SET_VISUAL_LAYER_DURATION',
      'RECALCULATE_ROWS',
      'REMOVE_AUDIO',
      'SHOW_VISUAL_AUDIO_GAP',
      'MANAGED_AUDIO_INSERT_DELETE',
      'AUDIO_MULTISELECT_DELETE',
      'REPLACE_VISUAL_TRACK',
    ]),
    ...mapActions(['SET_BG_MUSIC_BEATS', 'SET_BG_MUSIC_BEATS_STATUS_PROGRESS']),

    $_tmReady(yes) {
      if (yes) {
        // used to know when its ready after recovering after grid refresh
        this.$emit('timemachine-is-ready');
      }
    },

    $_tmBusy(isBusy) {
      app.timelineGateway.undoredo.toggleEnable(!isBusy);
    },

    /**
     * callable by undo-redo logic
     * @param undoRedoMeta
     * @param onDone
     */
    svbTimelineUpdate(undoRedoMeta, onDone) {
      const story = app.storyController.story;

      this.SET_SCROLL_TOP(undoRedoMeta.scrollTop);
      this.UPDATE_TIMEMACHINE_DATA({
        timelineData: story.getTimelineData(),
        lightboxAudio: this.selectedVoiceOverSegments,
        audioTracks: this.audioTracks,
      }).then(onDone);
    },

    $_timelineSeek(e) {
      if (app.abortKeyboard(e)) {
        return;
      }
      e.preventDefault();

      const currentTime = this.tmSecondhand;
      let nextTime = currentTime;
      if (this.tmMODE.RCP_MSG_PLAY) {
        nextTime = currentTime + (e.which === $.ui.keyCode.LEFT ? -1 : 1) * 5;
      } else {
        const frame = fg.index(currentTime);
        if (e.which === $.ui.keyCode.LEFT) {
          nextTime = fg.time(Math.max(1, frame - 1));
        } else {
          nextTime = fg.time(frame + 1);
        }
      }

      let timeBound = {
        min: 0,
        max: this.timelineDuration,
      };

      if (this.tmMODE.SCENE_EDIT) {
        const selectedAsset = this.tmSceneEscort.sceneTrack.data;
        timeBound = {
          min: selectedAsset.displayStartTime,
          max: selectedAsset.displayEndTime,
        };
      }

      nextTime = _.constrain(timeBound.min, nextTime, timeBound.max);

      if (this.tmMODE.RCP_MSG_PLAY) {
        storyViewBus.$emit('svbSeekRcp', nextTime, true);
      } else {
        storyViewBus.$emit('svbSeekRcp', nextTime, true);
        this.SET_SECONDHAND({ time: nextTime });
      }
    },

    $_timelineRemoveAsset(e, stash) {
      if (!app.abortKeyboard(e)) {
        e.preventDefault();

        if (this.tmMODE.OPTIONAL_AUDIO_INSERT) {
          this.MANAGED_AUDIO_INSERT_DELETE().then(() => {
            tmBus.$emit('callbackChange', { e });
          });
        }
        if (this.tmMODE.AUDIO_MULTISELECT) {
          this.AUDIO_MULTISELECT_DELETE().then(() => {
            tmBus.$emit('callbackChange', { e });
          });
        } else if (this.tmMODE.SCENE_EDIT) {
          const { overlay } = this.tmSelected;
          if (overlay) {
            this.REMOVE_VISUAL_OVERLAY({ overlay });
          }
        } else {
          const { visual, audio } = this.tmSelected;
          if (visual) {
            this.REMOVE_VISUAL(visual).then((res) => {
              this.UX_AFTER_REMOVE_VISUAL({
                removedTrackIndex: res.trackIndex,
                toTheLeft: stash.toTheLeft,
              }).then((res) => {
                tmBus.$emit('callbackAssetRemoved', {
                  e,
                  asset: visual.data,
                  seekToTime: res.seekToTime,
                });
              });
            });
          } else if (audio) {
            this.REMOVE_AUDIO(audio).then((res) => {
              this.UX_AFTER_REMOVE_AUDIO({
                removedTrackIndex: res.trackIndex,
                toTheLeft: stash.toTheLeft,
              }).then((res) => {
                tmBus.$emit('callbackAudioRemoved', {
                  e,
                  seekToTime: res.seekToTime,
                });
              });
            });
          }
        }
      }
    },

    svbTimelineZoom({ rowDuration }, onDone) {
      storyViewBus.$emit('svbPauseRcp');
      this.$once('timemachine-is-ready', onDone);
      this.CHANGE_ROW_DURATION(rowDuration);
    },

    svbTimelineRedrawAsset(track) {
      console.warn('svbTimelineRedrawAsset - are we there yet?');
    },

    svbTimelineHandleWallpaperReplace(...args) {
      app.timelineGateway.handleWallpaperReplace(...args);
    },

    svbRcpIsPlaying({ time, isPlaying }) {
      this.SET_SECONDHAND({ time });
      this.SET_MODE({ name: 'RCP_MSG_PLAY', value: isPlaying });
    },

    svbRcpCurrentTime({ time, updateSecondhand }) {
      if (updateSecondhand) {
        this.SET_SECONDHAND({ time });
      }
    },

    svbAppendToTimeline({ asset, visual = false, audio = false } = {}) {
      if (!this.tmMODE.SCENE_EDIT) {
        if (visual) {
          tmBus.$emit('callbackBeforeAddingExternalAsset', {
            external: true,
            asset,
          });
          this.INSERT_VISUAL_ASSET({
            asset,
            appendToEnd: true,
          });
          this.SET_VISUAL_LAYER_DURATION();
          this.RECALCULATE_ROWS();
        } else if (audio) {
          this.INSERT_AUDIO({
            track: asset.audioSegmentContent,
            appendToEnd: true,
          }).then(({ track }) => {
            tmBus.$emit('callbackRCPSeek', {
              time: track.data.displayStartTime,
              seekImmediate: true,
            });
          });
        }
        app.timelineGateway.serializeTimeline({ e: 'Clicked Add on Lightbox' });
      }
    },

    svbRemoveAudioTrack({ assetUUID }) {
      const audioTracks = this.trackLayer.audio;
      const tracksToRemove = audioTracks.filter(
        (track) => track.data.metadata.assetUUID === assetUUID
      );
      if (!tracksToRemove?.length) {
        return;
      }
      for (const track of tracksToRemove) {
        this.REMOVE_AUDIO(track);
      }
      app.timelineGateway.serializeTimeline({
        e: 'Removed audio from lightbox',
      });
    },

    // _onAfterAssetEdit
    svbTimelineAfterAssetEdit(editedAsset, options) {
      options = Object.assign(
        {
          changed: false,
          durationChange: null,
        },
        options
      );

      if (options.changed) {
        this.APPLY_EDITED_ASSET({
          editedAsset,
          durationChange: options.durationChange,
        }).then((track) => {
          if (track) {
            app.timelineGateway.decideRealTimePreview({
              visualTrack: track.data,
              resetRTP: options.changed,
            });

            app.timelineGateway.serializeTimeline(
              { e: 'afterAssetEdit' },
              { pause: false }
            );
            app.timelineGateway.undoredo.reset();
          } else {
            app.doctorController.report('onAfterAssetEdit -> track not found');
          }
        });
      }

      this.SELECT_VISUAL({ guid: editedAsset.guid });
    },

    svbTimelineRtp({ rtpModel, update = false }) {
      this.VISUAL_RTP_STATUS_CHANGED(rtpModel.attributes);

      if (update || rtpModel.get('status') === 'DONE') {
        if (!this.tmMODE.SCENE_EDIT) {
          app.timelineGateway.serializeTimelineDebounced();
        }
      }
    },

    svbShowVisualAudioGap() {
      this.SHOW_VISUAL_AUDIO_GAP();
    },

    // events {{ /////////////////////////////////////////////////////
    callbackChange(data) {
      app.timelineGateway.serializeTimeline(data);
    },

    callbackEditAsset(data) {
      storyViewBus.$emit('svbPauseRcp');
      app.namespace.Controller.AssetController.editTimelineAsset(data);
    },

    callbackEditSegment(track) {
      const isPip = !!track.pipa;
      if (!isPip) {
        storyViewBus.$emit('svbPauseRcp');
      }

      const asset = getOriginalVideoFromFragment(track.asset.data);
      const store = new AssetEditorStore(
        {
          openedFrom: 'timeline-change-segment',
          currentPageId: null,
          ian: {
            getAssets: () => {
              return [asset];
            },
          },
          timeInAsset: 0,
          isStoryboarder: app.rootStore.getters['user/isTemplateSpecialist'],
        },
        {
          storyModel: app.storyController.story,
          asset,
        }
      );

      app.rootStore.registerModule('assetEditor', store);
      app
        .showModal({
          type: 'custom',
          wrapperClass: ['wt2-asset-modal'],
          closeOnClickOut: false,
          showCloseButton: false,
          component: AssetModal,
          store: app.rootStore,
          componentPropsData: {},
        })
        .$on('close', async (e) => {
          if (e?.asset) {
            if (isPip) {
              const asset = createOverlayFromExternalAsset(
                _.cloneDeep(e.asset)
              );
              app.timelineGateway.fragmentDurationChanged(asset);
              track.pipa.changeOverlayTiming({
                oldOverlay: track.asset.data,
                newOverlay: asset,
              });

              tmBus.$emit('callbackSceneUpdate', {
                scene: track.pipa.scene,
                reflectUsage: true,
              });
              const time =
                track.pipa.scene.displayStartTime +
                track.asset.data.displayStartTime;
              this.SET_SECONDHAND({ time });
              tmBus.$emit('callbackRCPSeek', {
                time,
                seekImmediate: true,
              });
            } else {
              const asset = _.cloneDeep(e.asset);
              // @note: asset mutates after this call
              tmBus.$emit('callbackBeforeAddingExternalAsset', {
                external: true,
                asset,
                replacedAsset: track?.asset?.data,
              });
              const newTrack = await this.REPLACE_VISUAL_TRACK({
                track2replace: track.asset,
                asset: asset,
              });
              const seekToTime = newTrack?.data.displayStartTime + 0.001;
              tmBus.$emit('callbackChange', {
                e: 'Replaced segment from timeline',
                seekToTime,
              });
              tmBus.$emit('callbackAfterAddingExternalAsset', {
                track: newTrack,
              });
              this.SET_SECONDHAND({ time: seekToTime });
            }
          }
        });
    },

    callbackEditTranscript(track, origin) {
      if (!this.tmMODE.SCENE_EDIT) {
        this.UNSELECT_TRACKS();
        storyViewBus.$emit('svbPauseRcp');

        const list = getTranscriptEditableAssets(
          this.visualsWithExistingTranscript,
          this.trackLayer.audio,
          track
        );

        app.showModal({
          type: 'custom',
          component: wtTranscribe,
          showCloseButton: false,
          closeOnEscape: true,
          wrapperClass: ['wt-transcribe-modal'],
          store: app.rootStore,
          componentPropsData: {
            opener: origin,
            list,
            playheadTime: this.tmSecondhand,
          },
        });
      }
    },

    callbackEnterSceneEdit({ asset, autopilotEntering }) {
      app.analytics('analytics.scenes.enterSceneEditMode', {
        using: autopilotEntering ? 'drag' : 'menu',
      });

      const track = TimelineGatewayController.createPlayerVisualTrack({
        asset,
        ignoreRtp: true,
      });
      storyViewBus.$emit('svbSceneEnterRcp', {
        track,
        seekToTime: this.tmSecondhand,
      });
    },

    callbackLeaveSceneEdit() {
      storyViewBus.$emit('svbSceneLeaveRcp', {
        feed: app.timelineGateway.storyToPlayer(),
      });
    },

    callbackApplySceneEdit({ e, asset, analyticsData }) {
      app.timelineGateway.decideRealTimePreview({
        visualTrack: asset,
        resetRTP: false,
      });

      storyViewBus.$emit('svbSceneLeaveRcp', {
        feed: app.timelineGateway.storyToPlayer(),
      });

      app.timelineGateway.removeUnsupportedAGDForScene();
      app.timelineGateway.serializeTimeline({ e });

      if (analyticsData) {
        app.analytics('analytics.scenes.exitSceneEditMode', analyticsData);
      }
    },

    callbackCancelSceneEdit({ analyticsData }) {
      storyViewBus.$emit('svbSceneLeaveRcp', {
        feed: app.timelineGateway.storyToPlayer(),
      });

      if (analyticsData) {
        app.analytics('analytics.scenes.exitSceneEditMode', analyticsData);
      }
    },

    callbackSceneUpdate: _.throttle(
      function ({ e, scene, reflectUsage }) {
        const track = TimelineGatewayController.createPlayerVisualTrack({
          asset: scene,
          ignoreRtp: true,
        });
        storyViewBus.$emit('svbSceneUpdateRcp', { track });

        if (reflectUsage) {
          const tempAssets = app.timelineGateway
            .getVisualData()
            .map((asset) => {
              return asset.guid === scene.guid ? scene : asset;
            });
          app.timelineGateway.updateUsageMarkers(tempAssets);
        }
      },
      app.TIME.MENTAL.λ,
      { leading: true }
    ),

    callbackSceneOverlayInserted() {
      audioNotification.insertOverlayToScene();
    },

    callbackOverlayResizeStart({ overlay }) {
      storyViewBus.$emit('svbPauseRcp');
      app.timelineGateway.fragmentDurationChanged(overlay);
    },

    callbackOverlayResizeUpdate({ overlay }) {
      // NOOP
    },

    callbackOverlayResizeStop({ overlay }) {
      // NOOP
    },

    callbackAudioAssetResizeStart() {
      Analytics.trigger('VO:trimAudio');
    },

    callbackBeforeDragStarts() {
      storyViewBus.$emit('svbPauseRcp');
    },

    callbackBeforeAddingExternalAsset(data) {
      app.timelineGateway.timelineAddedVisualAsset(data);
    },

    callbackAfterAddingExternalAsset({ track }) {
      app.timelineGateway.decideRealTimePreview({
        visualTrack: track.data,
        resetRTP: false,
      });
    },

    callbackGetFragmentCutTimes({ track, fnCallback }) {
      let rv = null;
      const selectedAsset = _.find(
        app.rootStore.getters.selectedAssets,
        (selectedAsset) => {
          return (
            selectedAsset.metadata.assetUUID === track.data.metadata.assetUUID
          );
        }
      );

      if (
        selectedAsset &&
        (app.assetType.isFRAGMENT(selectedAsset) ||
          app.assetType.isSOCIALFRAGMENT(selectedAsset))
      ) {
        rv = _.pick(selectedAsset, ['startTime', 'endTime']);
      }

      fnCallback(rv);
    },

    callbackAssetResizeStart(asset) {
      storyViewBus.$emit('svbPauseRcp');

      app.realTimePreviewController.removeTrackFromPreviewQueue(asset);
      app.resetVisualTrackRTP(asset);
      app.timelineGateway.fragmentDurationChanged(asset);

      if (app.assetType.isFRAGMENT(asset)) {
        const track =
          app.timelineGateway.constructor.createSneakPeekVisualTrack(asset);
        storyViewBus.$emit('svbBeforeSneakPeekRcp', track);
      }
    },

    callbackAssetResizeUpdate({ asset, fromLeft }) {
      if (app.assetType.isFRAGMENT(asset) && asset.agd.videoSpeed === 1) {
        let time = 0;

        if (fromLeft) {
          time = asset.startTime;
        } else {
          time = asset.endTime;
        }

        storyViewBus.$emit('svbDuringSneakPeekRcp', time);
      }
    },

    callbackAssetResizeStop(asset) {
      app.timelineGateway.decideRealTimePreview({
        visualTrack: asset,
        delay: app.TIME.SECOND,
      });

      if (app.assetType.isFRAGMENT(asset)) {
        storyViewBus.$emit('svbAfterSneakPeek');
      }

      app.namespace.Controller.AssetController.setInitialAssetDuration(
        asset,
        asset.displayDuration
      );
    },

    callbackAudioAssetSplitStart(mode) {
      Analytics.trigger('VO:splitAudio', { mode: mode });
    },

    callbackAssetSplitStart(asset) {
      app.realTimePreviewController.removeTrackFromPreviewQueue(asset);
      app.resetVisualTrackRTP(asset);
      app.timelineGateway.fragmentDurationChanged(asset);
    },

    callbackAssetSplitStop(data) {
      app.timelineGateway.handleAssetSplit(data);
      app.timelineGateway.serializeTimeline({ e: data.e });
    },

    callbackRCPSeek({ time, seekImmediate = false }) {
      storyViewBus.$emit('svbSeekRcp', time, seekImmediate);
    },

    callbackSecondhandDragStop({ draggingFrom }) {
      app.analytics('analytics:secondhand:grab', {
        timeline: 'NOVO',
        from: _.capitalize(draggingFrom),
      });
    },

    callbackAssetRemoved({ e, asset, seekToTime }) {
      app.resetVisualTrackRTP(asset);
      app.timelineGateway.serializeTimeline({ e, seekToTime });
    },

    callbackAudioRemoved({ e, seekToTime }) {
      app.timelineGateway.serializeTimeline({ e, seekToTime });
    },

    // }} events /////////////////////////////////////////////////////
  },
};
</script>

<style lang="scss">
.wt-timeline-adapter {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
</style>
